import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OPDService } from '../../opd/opd.service';
import { IpdService } from '../ipd.service';
import { Iipd, IoperationSummary } from '../Iipd.type';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil, of, catchError, finalize } from 'rxjs';
import { TemplateListComponent } from '../../template/template-list/template-list.component';
import { TemplateType } from 'src/app/common/enums/template-type';
import { HttpClient } from '@angular/common/http';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { UserService } from '../../user/user.service';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { environment } from 'src/environments/environment';
import { IPatientInfo } from '../../patient/ipatient-info';
import { Iopd } from '../../opd/opddetails/iopddetails';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { DefaultValuePipe } from 'src/app/pipe/default-value.pipe';
import { ConvertTo24HourPipe } from 'src/app/pipe/convert-to24-hour.pipe';
import { PrintSettingService } from '../../settings/hospital-setting/print-setting/print-setting.service';
import { AddressUtils } from 'src/app/common/utils/address.utils';
import { PatientNameUtils } from 'src/app/common/utils/patient-name.utils';
import { formatDateOnlyForApi, parseApiDate } from 'src/app/common/function-helper';
import { CommonDatePipe } from 'src/app/pipe/common-date.pipe';
import { IPatientDailyReport } from 'src/app/shared/patient-daily-report/patient-daily-report.types';
import { PatientDailyReportService } from 'src/app/shared/patient-daily-report/patient-daily-report.service';
import { TimeConvertPipe } from 'src/app/pipe/time-convert.pipe';
import { FollowUpSlotPickerComponent, FollowUpSlotValue } from '../../appointment/follow-up-slot-picker/follow-up-slot-picker.component';
import { PrescriptionComponent } from '../../opd/prescription/prescription.component';
import { PrescriptionService } from '../../opd/prescription/prescription.service';
import { IPrescription, IPrescriptionItem } from '../../opd/prescription/iprescription';
import { DrugService } from '../../settings/drug-tab/drug-list/drug.service';
import { IDrugDetail } from '../../settings/drug-tab/drug-list/idrug';
import { MealRelation, MealRelationLabels } from 'src/app/common/enums/mealRelation-type';
import { SuggestedInvestigationPopupComponent } from '../../dialogs/suggested-investigation-popup/suggested-investigation-popup.component';
import { CategoryType } from 'src/app/common/enums/category-type';
import { LoginService } from 'src/app/login.service';
import { AbhaService } from '../../patient/abha-create-dialog/abha.service';
import { AbhaCareContext } from '../../patient/abha-create-dialog/iabha-info';

@Component({
  selector: 'app-ipd-discharge',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
    TranslateModule,
    FollowUpSlotPickerComponent,
    PrescriptionComponent
  ],
  providers: [
    ConvertTo24HourPipe,
    TimeConvertPipe,
    DatePipe,
    CommonDatePipe,
    TimeFormatPipe,
    DefaultValuePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './ipd-discharge.component.html',
  styleUrl: './ipd-discharge.component.scss'
})
export class IpdDischargeComponent implements OnInit, OnDestroy {
  dischargeForm: FormGroup;
  isUpdateMode: boolean = false;
  diagnosis: string = '';
  TemplateType = TemplateType; // Make enum available in template
  staticimagepath: string = environment.imagePath;
  defaultAvatarPath = 'assets/images/profile/user-1.jpg';
  showPatientInfo: boolean = false;
  minDate: Date;
  maxDate: Date = new Date();
  readOnly: boolean = false;
  isSaving: boolean = false;
  private dischargeLoadCount: number = 0;
  isDischargePersisted: boolean = false;
  private hasSavedChanges: boolean = false;
  isPrescriptionLoading: boolean = true;
  @Input() patientDetails: IPatientInfo;
  @Input() opdDetails: Iopd;
  @Input() ipdData: Iipd;
  careContext: AbhaCareContext | null = null;
  isLinkingAbha = false;
  @ViewChild(FollowUpSlotPickerComponent)followUpSlotPicker?: FollowUpSlotPickerComponent;
  @ViewChild(PrescriptionComponent) prescriptionComponent?: PrescriptionComponent;

  followUpSlotValue: FollowUpSlotValue = { appointDate: null, appointTime: null };
  followUpInitialValue: FollowUpSlotValue | null = null;

  onFollowUpValueChange(value: FollowUpSlotValue): void {
    this.followUpSlotValue = value;
  }

  onPrescriptionLoadingChange(isLoading: boolean): void {
    this.isPrescriptionLoading = isLoading;
  }

  get isDischargeDetailLoading(): boolean {
    return this.dischargeLoadCount > 0;
  }

  get isLoading(): boolean {
    return this.isDischargeDetailLoading || this.isPrescriptionLoading || this.isSaving;
  }

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    @Optional() private dialogRef: MatDialogRef<IpdDischargeComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      patientDetails?: IPatientInfo,
      opdDetails?: Iopd,
      ipdId?: number,
      ipdData?: Iipd,
      readOnly?: boolean
    } | null,
    private _opdService: OPDService,
    private _ipdService: IpdService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private http: HttpClient,
    private hospitalState: HospitalStateService,
    private userService: UserService,
    private datePipe: DatePipe,
    private commonDatePipe: CommonDatePipe,
    private timeFormatPipe: TimeFormatPipe,
    private loaderService: LoaderService,
    private defaultValuePipe: DefaultValuePipe,
    private convertTo24HourPipe: ConvertTo24HourPipe,
    private printSettingService: PrintSettingService,
    private patientDailyReportService: PatientDailyReportService,
    private timeConvertPipe: TimeConvertPipe,
    private prescriptionService: PrescriptionService,
    private drugService: DrugService,
    private loginService: LoginService,
    private abhaService: AbhaService
  ) {
    this.isUpdateMode = !!this.data?.ipdData;
    this.minDate = new Date(1800, 0, 1);
    this.readOnly = this.data?.readOnly ?? false;
  }

  ngOnInit(): void {
    const now = new Date();
    const currentTime = this.getCurrentTime();
    const dialogIpdData = this.data?.ipdData;
    this.isDischargePersisted = this.readOnly || this.hasPersistedDischarge(dialogIpdData);

    const dischargeDate = parseApiDate(dialogIpdData?.dischargeDate) ?? now;
    const dischargeTime = dialogIpdData?.dischargeTime
      ? this.convertTimeSpanToTimeInput(dialogIpdData.dischargeTime)
      : currentTime;

    this.dischargeForm = this.fb.group({
      dischargeDate: [dischargeDate, Validators.required],
      dischargeTime: [dischargeTime, Validators.required],
      diagnosis: [''],
      finalDiagnosis: [dialogIpdData?.finalDiagnosis || null, Validators.required],
      treatmentAdvice: [dialogIpdData?.treatmentAdvice || null],
      dischargeCondition: [dialogIpdData?.dischargeCondition || null]
    });

    // Load existing IPD data if in update mode
    if (this.isUpdateMode && this.data?.ipdId) {
      this.loadIPDData();
    }

    // Fetch diagnosis from OPD clinical if opdId is available
    if (this.data?.opdDetails?.id) {
      this.loadOPDClinical(this.data.opdDetails.id);
    } else if (dialogIpdData?.opdId) {
      this.loadOPDClinical(dialogIpdData.opdId);
    }

    this.applyFollowUpFromIpdData();
    this.loadCareContext();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private getCurrentTime(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private convertTimeSpanToTimeInput(timeSpan: string): string {
    if (!timeSpan) return this.getCurrentTime();

    // Handle TimeSpan format from backend (HH:mm:ss or HH:mm:ss.fffffff)
    const timePart = timeSpan.split('.')[0]; // Remove fractional seconds if present
    const parts = timePart.split(':');

    if (parts.length >= 2) {
      const hours = parts[0];
      const minutes = parts[1];
      // Return in HH:mm format for time input
      return `${hours}:${minutes}`;
    }

    return this.getCurrentTime();
  }

  private loadIPDData(): void {
    const ipdId = this.data?.ipdId;
    if (!ipdId) return;

    this.beginDischargeLoad();
    this._ipdService.getIPDById(ipdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => this.endDischargeLoad())
      )
      .subscribe({
        next: (response) => {
          if (response.result) {
            const ipdData = response.result;
            this.ipdData = ipdData;
            if (this.data) {
              this.data.ipdData = ipdData;
            }
            this.isDischargePersisted = this.readOnly || this.hasPersistedDischarge(ipdData);
            this.dischargeForm.patchValue({
              dischargeDate: parseApiDate(ipdData.dischargeDate) ?? new Date(),
              dischargeTime: ipdData.dischargeTime ? this.convertTimeSpanToTimeInput(ipdData.dischargeTime) : this.getCurrentTime(),
              finalDiagnosis: ipdData.finalDiagnosis || null,
              treatmentAdvice: ipdData.treatmentAdvice || null,
              dischargeCondition: ipdData.dischargeCondition || null
            });
            this.applyFollowUpFromIpdData();
          }
        },
        error: (error) => {
          console.error('Error loading IPD data:', error);
        }
      });
  }

  private loadOPDClinical(opdId: number): void {
    this.beginDischargeLoad();
    this._opdService.getOPDClinical(opdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => this.endDischargeLoad())
      )
      .subscribe({
        next: (res: any) => {
          const items = res?.result || [];
          if (items.length === 0) return;

          const diagnosisList = items
            .map((x: any) => x.primaryDiagnosis?.trim() || '')
            .filter((x: string) => x !== '');

          const diagnosisText = diagnosisList.join(', ');
          this.diagnosis = diagnosisText;

          // Update form control
          this.dischargeForm.patchValue({
            diagnosis: diagnosisText
          });
        },
        error: (error) => {
          console.error('Error loading OPD clinical data:', error);
        }
      });
  }

  private beginDischargeLoad(): void {
    this.dischargeLoadCount++;
  }

  private endDischargeLoad(): void {
    this.dischargeLoadCount = Math.max(0, this.dischargeLoadCount - 1);
  }

  copyDiagnosisToFinal(): void {
    const diagnosisValue = this.dischargeForm.get('diagnosis')?.value || '';
    this.dischargeForm.patchValue({
      finalDiagnosis: diagnosisValue
    });
  }

  openDiagnosisPopup(): void {
    const currentDiagnosis = this.dischargeForm.get('finalDiagnosis')?.value || '';
    const opdId = this.data?.opdDetails?.id
      || this.opdDetails?.id
      || this.data?.ipdData?.opdId
      || this.ipdData?.opdId;
    const dialogRef = this.dialog.open(SuggestedInvestigationPopupComponent, {
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        categoryType: CategoryType.Diagnosis,
        existingValue: currentDiagnosis,
        title: this.translate.instant('LABEL.SELECT_DIAGNOSIS'),
        opdId
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result !== undefined && result !== null) {
        this.dischargeForm.patchValue({ finalDiagnosis: result });
      }
    });
  }

  get followUpDoctorId(): number | null {
    const doctorId = this.data?.ipdData?.doctorId
      || this.ipdData?.doctorId
      || this.data?.opdDetails?.doctorId
      || this.opdDetails?.doctorId;
    return doctorId ? Number(doctorId) : null;
  }

  get followUpPatientId(): number | null {
    const patientId = this.data?.patientDetails?.id || this.patientDetails?.id;
    return patientId ? Number(patientId) : null;
  }

  get followUpIpdId(): number | null {
    const ipdId = this.data?.ipdId || this.data?.ipdData?.id || this.ipdData?.id;
    return ipdId ? Number(ipdId) : null;
  }

  get canLinkAbha(): boolean {
    const patient = this.data?.patientDetails || this.patientDetails;
    return this.loginService.isAbdmHipEnabled()
      && this.isDischargePersisted
      && !!patient?.abhaNumber?.trim()
      && !!this.followUpIpdId;
  }

  get abhaLinkLabel(): string {
    if (this.careContext?.status === 'Linked') {
      return this.translate.instant('ABHA.LINKED');
    }

    return this.translate.instant('ABHA.LINK_VISIT');
  }

  get isAbhaLinkDisabled(): boolean {
    return this.isLinkingAbha || this.isAbhaLinkInProgress;
  }

  private get isAbhaLinkInProgress(): boolean {
    return ['InitSent', 'UserAuthenticating', 'Linked'].includes(this.careContext?.status || '');
  }

  linkAdmissionToAbha(): void {
    const ipdId = this.followUpIpdId;
    if (!ipdId || this.isAbhaLinkDisabled) {
      return;
    }

    this.isLinkingAbha = true;
    this.abhaService.linkCareContext({ ipdId }).pipe(takeUntil(this._unsubscribeAll)).subscribe(response => {
      this.isLinkingAbha = false;
      if (response?.isSuccess && response.result) {
        this.careContext = response.result;
        this.toastr.success(this.translate.instant('ABHA.LINK_REQUESTED'));
        return;
      }

      const message = response?.errors?.length ? response.errors.join('\n') : response?.message;
      this.toastr.error(message || this.translate.instant('ABHA.LINK_FAILED'));
    });
  }

  private loadCareContext(): void {
    const ipdId = this.followUpIpdId;
    if (!ipdId || !this.loginService.isAbdmHipEnabled()) {
      return;
    }

    this.abhaService.getCareContexts({ ipdId })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(response => this.careContext = response.result?.[0] || null);
  }

  private applyFollowUpFromIpdData(): void {
    const ipd = this.data?.ipdData || this.ipdData;
    if (!ipd?.followUpDate || !ipd.followUpTime) {
      this.followUpInitialValue = null;
      this.followUpSlotValue = { appointDate: null, appointTime: null };
      return;
    }

    this.followUpInitialValue = {
      appointDate: parseApiDate(ipd.followUpDate),
      appointTime: ipd.followUpTime,
    };
    this.followUpSlotValue = this.followUpInitialValue;
  }

  openTemplatePopupforTreatmentAdvice(): void {
    const templateDialog = this.dialog.open(TemplateListComponent, {
     autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        templateType: TemplateType.TreatmentAdvice
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the treatment advice field
        const currentValue = this.dischargeForm.get('treatmentAdvice')?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.dischargeForm.get('treatmentAdvice')?.setValue(combinedText);
      }
    });
  }

  openTemplatePopup(): void {
    const templateDialog = this.dialog.open(TemplateListComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        templateType: TemplateType.DischargeCondition
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe((result) => {
      if (result && result.selected && result.template) {
        // Get the current value of the discharge condition field
        const currentValue = this.dischargeForm.get('dischargeCondition')?.value || '';
        const templateText = result.template.templateDescription || '';

        const parts = [currentValue.trim(), templateText.trim()].filter(Boolean);
        const combinedText = parts.join('\n');

        this.dischargeForm.get('dischargeCondition')?.setValue(combinedText);
      }
    });
  }

  save(): void {
    if (this.dischargeForm.invalid) {
      this.dischargeForm.markAllAsTouched();
      return;
    }

    // Check for partial follow-up selection — use @ViewChild first, fall back to cached value
    const hasPartialSelection = this.followUpSlotPicker?.hasPartialSelection
      ?? (!!(this.followUpSlotValue.appointDate || this.followUpSlotValue.appointTime)
          && !(this.followUpSlotValue.appointDate && this.followUpSlotValue.appointTime));

    if (hasPartialSelection) {
      this.followUpSlotPicker?.markAllAsTouched();
      const slotVal = this.followUpSlotPicker?.value ?? this.followUpSlotValue;
      const messageKey = slotVal.appointDate ? 'REQUIRED.SELECT_TIME' : 'REQUIRED.SELECT_DATE';
      this.toastr.error(this.translate.instant(messageKey));
      return;
    }

    const ipdId = this.data?.ipdId;
    if (!ipdId) {
      this.toastr.error('IPD ID is missing');
      return;
    }

    const prescriptionSave = this.prescriptionComponent?.getPayloadForParentSave() ?? { isValid: true, payload: null };
    if (!prescriptionSave.isValid) {
      return;
    }

    this.isSaving = true;
    // Get current IPD data first
    this._ipdService.getIPDById(ipdId)
      .pipe(
        takeUntil(this._unsubscribeAll),
      )
      .subscribe({
        next: (ipdResponse) => {
          if (!ipdResponse.result) {
            this.isSaving = false;
            this.toastr.error('IPD data not found');
            return;
          }

          const formValue = this.dischargeForm.value;
          const existingIpd = ipdResponse.result;
          const followUpSlot = this.followUpSlotPicker?.value ?? this.followUpSlotValue;

          // Prepare update payload
          const payload: Iipd = {
            ...existingIpd,
            dischargeDate: formatDateOnlyForApi(formValue.dischargeDate) || undefined,
            dischargeTime: this.convertTo24HourPipe.transform(formValue.dischargeTime),
            finalDiagnosis: formValue.finalDiagnosis || null,
            treatmentAdvice: formValue.treatmentAdvice || null,
            dischargeCondition: formValue.dischargeCondition || null,
            followUpDate: followUpSlot.appointDate ? formatDateOnlyForApi(followUpSlot.appointDate) : null,
            followUpTime: followUpSlot.appointTime ? this.timeConvertPipe.transform(followUpSlot.appointTime) : null
          };

          // Update IPD
          this._ipdService.dischargeIPD(payload).pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
              next: (response) => {
                if (response.isSuccess) {
                  this.savePrescriptionAfterDischarge(prescriptionSave.payload, payload);
                } else {
                  this.isSaving = false;
                  const allErrors = Object.values(response.errors || {}).flat();
                  this.toastr.error(allErrors.join('\n'));
                }
              },
              error: (error) => {
                this.isSaving = false;
                const errorMessage = error?.error?.message || 'Error updating discharge information';
                this.toastr.error(errorMessage);
              }
            });
        },
        error: (error) => {
          this.isSaving = false;
          const errorMessage = error?.error?.message || 'Error loading IPD data';
          this.toastr.error(errorMessage);
        }
      });
  }

  private savePrescriptionAfterDischarge(prescriptionPayload: IPrescription | null, savedIpdData: Iipd): void {
    const successMessage = prescriptionPayload
      ? this.translate.instant('LABEL.DISCHARGE_PRESCRIPTION_SAVED')
      : this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('LABEL.DISCHARGE'));

    if (!prescriptionPayload) {
      this.finishSave(savedIpdData, successMessage);
      return;
    }

    const apiCall = prescriptionPayload.id && prescriptionPayload.id > 0
      ? this.prescriptionService.updatePrescription(prescriptionPayload)
      : this.prescriptionService.createPrescription(prescriptionPayload);

    apiCall.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.finishSave(savedIpdData, successMessage);
        } else {
          this.isSaving = false;
          const allErrors = Object.values(response.errors || {}).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      },
      error: (error) => {
        this.isSaving = false;
        const errorMessage = error?.error?.message || 'Error saving prescription';
        this.toastr.error(errorMessage);
      }
    });
  }

  private finishSave(savedIpdData: Iipd, successMessage?: string): void {
    this.isSaving = false;
    this.ipdData = savedIpdData;
    if (this.data) {
      this.data.ipdData = savedIpdData;
    }
    this.isUpdateMode = true;
    this.readOnly = true;
    this.isDischargePersisted = this.hasPersistedDischarge(savedIpdData);
    this.hasSavedChanges = true;
    this.applyFollowUpFromIpdData();
    this.loadCareContext();

    if (successMessage) {
      this.toastr.success(successMessage);
    }
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close(this.hasSavedChanges || undefined);
    }
  }

  private hasPersistedDischarge(ipdData?: Iipd | null): boolean {
    return !!ipdData?.dischargeDate && !!ipdData.dischargeTime;
  }

  async printDischargeDetails(): Promise<void> {
    const ipdId = this.data?.ipdId || this.ipdData?.id;
    const patientInfo = this.data?.patientDetails || this.patientDetails;
    const opdData = this.data?.opdDetails || this.opdDetails;

    if (!ipdId || !patientInfo || !opdData) {
      this.toastr.error('Missing required data for printing');
      return;
    }

    try {
      this.loaderService.show();
      // Load IPD data, operation summary and daily progress notes in parallel
      const [ipdResponse, operationSummaryResponse, dailyReportResponse, prescriptionResponse, drugResponse] = await Promise.all([
        this._ipdService.getIPDById(ipdId).pipe(
          catchError(() => of({ result: null, isSuccess: false }))
        ).toPromise(),
        this._ipdService.getOperationSummaryByIpdId(ipdId).pipe(
          catchError(() => of({ result: null, isSuccess: false }))
        ).toPromise(),
        this.patientDailyReportService.list({
          patientId: patientInfo.id,
          opdId: opdData.id ?? null,
          ipdId
        }).pipe(
          catchError(() => of({ result: [], isSuccess: false }))
        ).toPromise(),
        this.prescriptionService.getPrescriptionDetailByIPDId(ipdId).pipe(
          catchError(() => of({ result: null, isSuccess: false }))
        ).toPromise(),
        this.drugService.getDrugDetails().pipe(
          catchError(() => of({ result: [], isSuccess: false }))
        ).toPromise()
      ]);

      const loadedIpdData = ipdResponse?.result || this.data?.ipdData || this.ipdData;
      const operationSummary = operationSummaryResponse?.result || null;
      const dailyReports = dailyReportResponse?.result ?? [];
      const prescription = prescriptionResponse?.result ?? null;
      const drugs = drugResponse?.result ?? [];

      if (!loadedIpdData) {
        this.toastr.error('IPD data not found');
        return;
      }

      const printContent = await this.createDischargePrintContent(loadedIpdData, operationSummary, dailyReports, prescription, drugs);
      this.openPrintWindow(printContent);
    } catch (error) {
      console.error('Error printing discharge details:', error);
      this.toastr.error('Error loading data for printing');
    } finally {
      this.loaderService.hide();
    }
  }

  private async createDischargePrintContent(
    ipdData: Iipd,
    operationSummary: IoperationSummary | null,
    dailyReports: IPatientDailyReport[],
    prescription: IPrescription | null,
    drugs: IDrugDetail[]
  ): Promise<string> {
    try {
      let template =
        (await this.http
          .get('assets/print-template.html', { responseType: 'text' })
          .toPromise()) || '';

      const hospital = this.hospitalState.getHospital();
      const opdData = this.data?.opdDetails || this.opdDetails;
      const patientInfo = this.data?.patientDetails || this.patientDetails;
      const doctorId = opdData?.doctorId || ipdData.doctorId;

      const [doctor, settings] = await Promise.all([
        this.userService.getUserByID(doctorId).toPromise(),
        this.printSettingService.getPrintSettingPromise(hospital.id)
      ]);

      const doctorName = `${this.defaultValuePipe.transform(doctor?.result?.firstName, '')} ${this.defaultValuePipe.transform(doctor?.result?.lastName, '')}`.trim();
      const hasPrintValue = (value: unknown): boolean => {
        const text = value?.toString().trim();
        return !!text && text !== '-';
      };
      const printValue = (value: unknown): string => this.escapeHtml(value);
      const printLine = (label: string, value: unknown): string =>
        hasPrintValue(value) ? `<strong>${label}:</strong> ${printValue(value)}<br/>` : '';
      const printBlock = (label: string, value: unknown): string =>
        hasPrintValue(value)
          ? `<div style="margin-top:15px; font-size:13px;"><strong>${label}:</strong><br/><span style="white-space:pre-wrap;">${printValue(value)}</span></div>`
          : '';
      const printCell = (label: string, value: unknown): string =>
        hasPrintValue(value)
          ? `<td style="border:1px solid #000; padding:6px;"><strong>${label}:</strong> ${printValue(value)}</td>`
          : '<td style="border:1px solid #000; padding:6px;"></td>';
      const printRow = (cells: string[], values: unknown[]): string =>
        values.some((value) => hasPrintValue(value))
          ? `<tr>${cells.join('')}</tr>`
          : '';
      const patientAddress = AddressUtils.formatPatientAddress(patientInfo);
      const patientAddressHtml = printLine('Address', patientAddress);
      const patientPhoneHtml = printLine(
        'Ph',
        patientInfo?.contactInfo?.[0]?.contact,
      );
      const opdDate = opdData?.opdDate
        ? this.defaultValuePipe.transform(this.commonDatePipe.transform(opdData?.opdDate), '-')
        : null;
      const ipdDate = this.defaultValuePipe.transform(this.datePipe.transform(ipdData.ipdDate, 'dd-MM-yyyy'), '-');
      const admittedAt = this.defaultValuePipe.transform(this.timeFormatPipe.transform(ipdData.ipdTime), '-');
      const dischargeDate = ipdData.dischargeDate
        ? this.defaultValuePipe.transform(this.datePipe.transform(ipdData.dischargeDate, 'dd-MM-yyyy'), '-')
        : null;
      const dischargeTime = ipdData.dischargeTime
        ? this.defaultValuePipe.transform(this.timeFormatPipe.transform(ipdData.dischargeTime), '-')
        : null;
      const followUpDate = ipdData.followUpDate
        ? this.defaultValuePipe.transform(this.datePipe.transform(ipdData.followUpDate, 'dd-MM-yyyy'), '-')
        : null;
      const followUpTime = ipdData.followUpTime
        ? this.defaultValuePipe.transform(this.timeFormatPipe.transform(ipdData.followUpTime), '-')
        : null;
      const patientFullName = PatientNameUtils.formatFullName(patientInfo);
      const patientNameHtml = printLine('Patient Name', patientFullName);
      const patientAgeGender = [patientInfo?.age, patientInfo?.gender]
        .map((value) => value?.toString().trim())
        .filter((value): value is string => !!value && value !== '-')
        .join(' / ');
      const ipdDetailsTableHtml = [
        printRow(
          [
            printCell('O.P.D No', opdData?.opdNumber),
            printCell('O.P.D Date', opdDate),
            printCell('Ward', ipdData.wardName),
          ],
          [opdData?.opdNumber, opdDate, ipdData.wardName],
        ),
        printRow(
          [
            printCell('I.P.D No', ipdData.ipdNumber),
            printCell('I.P.D Date', ipdDate),
            printCell('Room No', ipdData.roomNo),
          ],
          [ipdData.ipdNumber, ipdDate, ipdData.roomNo],
        ),
        printRow(
          [
            printCell('Admitted On', ipdDate),
            printCell('Admitted At', admittedAt),
            printCell('Bed No', ipdData.bedNo),
          ],
          [ipdDate, admittedAt, ipdData.bedNo],
        ),
        printRow(
          [
            printCell('Discharge Date', dischargeDate),
            printCell('Discharge Time', dischargeTime),
            '<td style="border:1px solid #000; padding:6px;"></td>',
          ],
          [dischargeDate, dischargeTime],
        ),
      ].join('');
      const assistantDoctorHtml = hasPrintValue(ipdData.assitantDoctorName)
        ? `<div style="margin-top:15px; font-size:13px;"><strong>Assistant Doctor:</strong> ${this.escapeHtml(ipdData.assitantDoctorName)}</div>`
        : '';
      const anesthetistHtml = hasPrintValue(ipdData.anesthetisName)
        ? `<div style="margin-top:15px; font-size:13px;"><strong>Anesthetist:</strong> ${this.escapeHtml(ipdData.anesthetisName)}</div>`
        : '';
      const relativeNameAddressHtml = hasPrintValue(ipdData.nameAndAddress)
        ? `<strong>Name & Address of relative / friend attending patient:</strong><br/><span style="white-space:pre-wrap;">${this.escapeHtml(ipdData.nameAndAddress)}</span><br/>`
        : '';
      const relativeContactHtml = printLine('Tel No', ipdData.contact);
      const relativeDetailsHtml =
        relativeNameAddressHtml || relativeContactHtml
          ? `<div style="margin-top:15px; font-size:13px;">${relativeNameAddressHtml}${relativeContactHtml}</div>`
          : '';
      const nextVisitOn = hasPrintValue(followUpDate) && hasPrintValue(followUpTime)
        ? `${followUpDate} at ${followUpTime}`
        : [followUpDate, followUpTime]
            .filter((value) => hasPrintValue(value))
            .join(' ');
      const nextVisitHtml = hasPrintValue(nextVisitOn)
        ? `<div style="margin-top:15px; font-size:13px;"><strong>Next Visit On:</strong> ${printValue(nextVisitOn)}</div>`
        : '';
      const dailyProgressNotesHtml = this.buildDailyProgressNotesHtml(dailyReports);
      const prescriptionHtml = this.buildPrescriptionHtml(prescription, drugs);

      // IPD Details Section
      const ipdDetailsHtml = `
        <h2 style="text-align:center; margin:10px 0; text-decoration:underline;">
          IPD DISCHARGE DETAILS
        </h2>

        <div style="display:flex; gap:12px; border:1px solid #000; padding:8px; margin:10px 0;">
          <div style="flex:1; font-size:13px;">
            ${patientNameHtml}
            ${patientAddressHtml}
            ${patientPhoneHtml}
          </div>
        </div>

        <table style="width:100%; border-collapse:collapse; margin-top:18px; font-size:13px;">
          ${ipdDetailsTableHtml}
         
        </table>

        ${assistantDoctorHtml}

        ${anesthetistHtml}

        ${printBlock('Anesthesia', operationSummary?.anesthesia)}

        ${printBlock('History Notes / Pre-Operative notes', operationSummary?.historyNotes)}

        ${printBlock('Nature of Treatment', operationSummary?.natureOfTreatmentName)}
        ${printBlock('Investigation', operationSummary?.investigation)}
        ${printBlock('Operation Note', operationSummary?.operationNotes)}
        ${printBlock('Treatment Given', operationSummary?.treatmentGiven)}

        ${printBlock('Final Diagnosis', ipdData.finalDiagnosis)}

        ${dailyProgressNotesHtml}

        ${printBlock('Treatment Advice', ipdData.treatmentAdvice)}

        ${printBlock('Discharge Condition', ipdData.dischargeCondition)}
        
        ${prescriptionHtml}

        ${relativeDetailsHtml}

        ${nextVisitHtml}
      `;

      const reportSections = ipdDetailsHtml;

      const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;
      const fullImagePath = hospital?.logo ? `${this.staticimagepath}${hospital.logo}` : null;

      const replacements: Record<string, string> = {
        reportTitle: this.escapeHtml(patientFullName ? `Discharge Details - ${patientFullName}` : 'Discharge Details'),
        hospitalName: this.escapeHtml(this.defaultValuePipe.transform(hospital?.name, 'Hospital Name')),
        hospitalAddress: this.escapeHtml(this.defaultValuePipe.transform(hospital?.address, '')),
        phone: this.escapeHtml(this.defaultValuePipe.transform(hospital?.phone, '')),
        reportSections: reportSections,
        doctorName: this.escapeHtml(doctorName),
        qualification: this.escapeHtml(this.defaultValuePipe.transform(doctor?.result?.degree, '')),
        doctorRegistrationNo: doctor?.result?.registrationNumber
          ? this.escapeHtml(`Reg No. ${this.defaultValuePipe.transform(doctor?.result?.registrationNumber, '')}`)
          : '',
        doctorPhone: doctor?.result?.phoneNumber
          ? this.escapeHtml(`M. ${this.defaultValuePipe.transform(doctor?.result?.phoneNumber, '')}`)
          : '',
        logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`,
        patientName: this.escapeHtml(patientFullName),
        patientAge: this.escapeHtml(patientAgeGender),
        opdNumber: hasPrintValue(opdData?.opdNumber) ? this.escapeHtml(opdData?.opdNumber) : '',
        opdDate: hasPrintValue(opdDate) ? this.escapeHtml(opdDate) : ''
      };

      let filledTemplate = template;
      for (const key in replacements) {
        const value = replacements[key] ?? '';
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        filledTemplate = filledTemplate.replace(regex, value);
      }

      // Apply settings
      let styles = '';
      if (settings) {
        const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
        const headerClass = settings.logoPosition === 'center' ? 'center' : '';
        const headerAlignment = settings.addressAlignment || 'left';

        styles = `
            :root {
                --logo-size: ${settings.logoSize || '60px'};
                --logo-position: ${logoPos};
                --header-alignment: ${headerAlignment};
                --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                --address-font-size: ${settings.addressFontSize || 13}px;
                --footer-display: ${settings.showFooter ? 'block' : 'none'};
                --doctor-signature-top-display: ${settings.showFooter && settings.doctorNamePosition === 'top' ? 'block' : 'none'};
                --doctor-signature-bottom-display: ${settings.showFooter && settings.doctorNamePosition === 'footer' ? 'block' : 'none'};
            }
        `;
        filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
      } else {
        filledTemplate = filledTemplate.replace('{{headerClass}}', '');
      }

      return filledTemplate;
    } catch (error) {
      console.error('Error creating discharge print content:', error);
      throw error;
    }
  }

  private buildDailyProgressNotesHtml(dailyReports: IPatientDailyReport[]): string {
    const reports = [...(dailyReports ?? [])]
      .sort((a, b) => this.getDailyReportSortValue(a) - this.getDailyReportSortValue(b));

    if (!reports.length) {
      return '';
    }

    const rows = reports.map(report => {
      const reportDate = this.defaultValuePipe.transform(this.commonDatePipe.transform(report.reportDate), '-');
      const reportTime = report.reportTime
        ? this.defaultValuePipe.transform(this.timeFormatPipe.transform(report.reportTime), '-')
        : '-';
      const description = this.getDailyReportSummary(report);

      return `
        <tr>
          <td style="border:1px solid #000; padding:6px; width:20%;">${reportDate}</td>
          <td style="border:1px solid #000; padding:6px; width:16%;">${reportTime}</td>
          <td style="border:1px solid #000; padding:6px; white-space:pre-wrap;">${this.escapeHtml(description)}</td>
        </tr>
      `;
    }).join('');

    return `
      <div style="margin-top:18px; font-size:13px; break-inside:avoid; page-break-inside:avoid;">
        <strong>Daily Progress Notes:</strong>
        <table style="width:100%; border-collapse:collapse; margin-top:8px; font-size:13px;">
          <thead>
            <tr>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:20%;">Date</th>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:16%;">Time</th>
              <th style="border:1px solid #000; padding:6px; text-align:left;">Description</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  private buildPrescriptionHtml(prescription: IPrescription | null, drugs: IDrugDetail[]): string {
    const items = prescription?.prescriptionItems ?? [];

    if (!items.length) {
      return '';
    }

    const rows = items.map(item => `
      <tr>
        <td style="border:1px solid #000; padding:6px;">${this.escapeHtml(this.getPrescriptionDrugName(item, drugs))}</td>
        <td style="border:1px solid #000; padding:6px;">${this.escapeHtml(item.dosePattern)}</td>
        <td style="border:1px solid #000; padding:6px;">${this.escapeHtml(item.duration)}</td>
        <td style="border:1px solid #000; padding:6px;">${this.escapeHtml(this.getPrescriptionMealRelationName(item.mealRelation))}</td>
        <td style="border:1px solid #000; padding:6px; white-space:pre-wrap;">${this.escapeHtml(item.instruction)}</td>
      </tr>
    `).join('');

    return `
      <div style="margin-top:18px; font-size:13px; break-inside:avoid; page-break-inside:avoid;">
        <strong>Prescription:</strong>
        <table style="width:100%; border-collapse:collapse; margin-top:8px; font-size:13px;">
          <thead>
            <tr>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:25%;">Drug</th>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:15%;">Dose Pattern</th>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:15%;">Duration</th>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:20%;">Meal Relation</th>
              <th style="border:1px solid #000; padding:6px; text-align:left; width:25%;">Instruction</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  private getPrescriptionDrugName(item: IPrescriptionItem, drugs: IDrugDetail[]): string {
    return item.drugName || drugs.find(drug => drug.id === item.drugId)?.name || '';
  }

  private escapeHtml(value: unknown): string {
    return (value ?? '').toString().replace(/[&<>"']/g, (char) => {
      const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };

      return entities[char];
    });
  }

  private getPrescriptionMealRelationName(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'string') {
      const enumValue = MealRelation[value as keyof typeof MealRelation];
      return MealRelationLabels[enumValue as MealRelation] ?? value;
    }

    return MealRelationLabels[value as MealRelation] ?? '';
  }

  private getDailyReportSummary(report: IPatientDailyReport): string {
    return [
      report.dailynotes,
      report.plan
    ].find(value => !!value?.trim()) ?? 'Medicine Orders';
  }

  private getDailyReportSortValue(report: IPatientDailyReport): number {
    const dateValue = report.reportDate ? new Date(report.reportDate).getTime() : 0;
    const timeParts = report.reportTime?.toString().split(':').map(Number) ?? [];
    const hours = Number.isFinite(timeParts[0]) ? timeParts[0] : 0;
    const minutes = Number.isFinite(timeParts[1]) ? timeParts[1] : 0;
    const seconds = Number.isFinite(timeParts[2]) ? timeParts[2] : 0;

    return dateValue + (hours * 60 * 60 + minutes * 60 + seconds) * 1000;
  }

  private openPrintWindow(content: string): void {

    let iframe = document.getElementById('print-frame') as HTMLIFrameElement;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'print-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow?.document;

    if (!doc) {
      return;
    }

    doc.open();
    doc.write(content);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.print();
    }, 300);
  }
}
